import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Eye,
  Download,
  Filter,
  Search,
  TrendingUp,
  DollarSign
} from 'lucide-react';

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'newest'
  });
  const [loading, setLoading] = useState(true);

  // Mock data
  const mockApplications = [
    {
      id: 1,
      companyName: "EcoVerify International",
      applicantName: "Dr. Sarah Mitchell",
      email: "sarah@ecoverify.com",
      status: "pending",
      submittedDate: "2024-01-20T10:30:00Z",
      yearsOfExperience: 12,
      totalProjectsVerified: 150,
      supportedStandards: ["Gold Standard", "VCS", "CDM"],
      geographicCoverage: ["North America", "Europe", "Asia"],
      reviewScore: null,
      reviewNotes: "",
      documentsComplete: true,
      referencesVerified: false
    },
    {
      id: 2,
      companyName: "GreenCert Solutions",
      applicantName: "Michael Rodriguez",
      email: "m.rodriguez@greencert.org",
      status: "under_review",
      submittedDate: "2024-01-18T14:15:00Z",
      yearsOfExperience: 8,
      totalProjectsVerified: 89,
      supportedStandards: ["VCS", "ACR", "CAR"],
      geographicCoverage: ["Latin America", "North America"],
      reviewScore: 8.5,
      reviewNotes: "Strong technical capabilities, good references",
      documentsComplete: true,
      referencesVerified: true
    },
    {
      id: 3,
      companyName: "Carbon Integrity Ltd",
      applicantName: "Dr. Priya Sharma",
      email: "priya@carbonintegrity.com",
      status: "approved",
      submittedDate: "2024-01-15T09:45:00Z",
      yearsOfExperience: 15,
      totalProjectsVerified: 220,
      supportedStandards: ["Gold Standard", "VCS", "Plan Vivo"],
      geographicCoverage: ["Asia", "Africa", "Europe"],
      reviewScore: 9.2,
      reviewNotes: "Excellent credentials, highly recommended",
      documentsComplete: true,
      referencesVerified: true,
      approvedDate: "2024-01-22T16:30:00Z"
    },
    {
      id: 4,
      companyName: "TechVerify Corp",
      applicantName: "James Wilson",
      email: "james@techverify.io",
      status: "rejected",
      submittedDate: "2024-01-12T11:20:00Z",
      yearsOfExperience: 3,
      totalProjectsVerified: 15,
      supportedStandards: ["VCS"],
      geographicCoverage: ["North America"],
      reviewScore: 4.2,
      reviewNotes: "Insufficient experience, limited geographic coverage",
      documentsComplete: false,
      referencesVerified: false,
      rejectedDate: "2024-01-19T13:45:00Z"
    }
  ];

  const mockStats = {
    totalApplications: 47,
    pendingReview: 12,
    approved: 28,
    rejected: 7,
    avgProcessingTime: 6.5, // days
    activeVerifiers: 28,
    totalCommissionThisMonth: 15750,
    avgApplicationScore: 7.8
  };

  useEffect(() => {
    // Simulate API calls
    setTimeout(() => {
      setApplications(mockApplications);
      setStats(mockStats);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'under_review':
        return <Eye className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesStatus = !filters.status || app.status === filters.status;
    const matchesSearch = !filters.search || 
      app.companyName.toLowerCase().includes(filters.search.toLowerCase()) ||
      app.applicantName.toLowerCase().includes(filters.search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600">Manage verifier applications and platform operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
              <p className="text-xs text-blue-600">+3 this week</p>
            </div>
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingReview}</p>
              <p className="text-xs text-yellow-600">Requires attention</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Verifiers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeVerifiers}</p>
              <p className="text-xs text-green-600">+2 this month</p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Commission Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalCommissionThisMonth?.toLocaleString()}</p>
              <p className="text-xs text-purple-600">This month</p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search applications..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="experience">Most Experience</option>
            <option value="score">Highest Score</option>
          </select>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Verifier Applications</h3>
          <p className="text-sm text-gray-600">Showing {filteredApplications.length} applications</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company & Applicant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Experience
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredApplications.map((application) => (
                <tr key={application.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{application.companyName}</p>
                      <p className="text-sm text-gray-600">{application.applicantName}</p>
                      <p className="text-xs text-gray-500">{application.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="text-gray-900">{application.yearsOfExperience} years</p>
                      <p className="text-gray-600">{application.totalProjectsVerified} projects</p>
                      <p className="text-xs text-gray-500">{application.geographicCoverage.length} regions</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                      {getStatusIcon(application.status)}
                      <span className="ml-1 capitalize">{application.status.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {application.reviewScore ? (
                      <div className="flex items-center">
                        <span className="font-medium">{application.reviewScore}/10</span>
                        <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${(application.reviewScore / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">Not scored</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {new Date(application.submittedDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Link
                        to={`/admin/applications/${application.id}`}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Review Application"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <button
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title="Download Documents"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Quick Actions</h3>
          <div className="space-y-2">
            <Link
              to="/admin/bulk-approve"
              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
            >
              Bulk Approve Applications
            </Link>
            <Link
              to="/admin/export"
              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
            >
              Export Application Data
            </Link>
            <Link
              to="/admin/settings"
              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
            >
              Update Review Criteria
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Performance Metrics</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Avg Processing Time:</span>
              <span className="font-medium">{stats.avgProcessingTime} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Approval Rate:</span>
              <span className="font-medium">80%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Avg Application Score:</span>
              <span className="font-medium">{stats.avgApplicationScore}/10</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Recent Activity</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-gray-700">Approved EcoVerify Ltd</span>
            </div>
            <div className="flex items-center">
              <Eye className="h-4 w-4 text-blue-600 mr-2" />
              <span className="text-gray-700">Reviewing GreenCert Solutions</span>
            </div>
            <div className="flex items-center">
              <XCircle className="h-4 w-4 text-red-600 mr-2" />
              <span className="text-gray-700">Rejected TechVerify Corp</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 